import { pageUrls } from 'globalconfig/constants/keys';
import { BACKEND_DATE_FORMAT } from 'globalconfig/constants/static';
import { objectArrayFromDiffFields } from 'globalutils/fmUtils';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { lazy, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object } from 'yup';
import RenderComponent from './RenderComponent';
import { getSchema } from './schema';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));

const StateForm = ({
  Styled,
  isEdit = false,
  history,
  vehicles,
  states,
  drivers,
  callback,
  addUpdateIFTAState,
  validateOdometer,
  iftaDetails,
  fetchLatLong,
}) => {
  const { i18n } = useTranslation();

  const activeVehicles = objectArrayFromDiffFields(vehicles?.vehicles, 'id', 'unit_id');
  const deActivatedVehicles = objectArrayFromDiffFields(vehicles?.deactivatedVehicles, 'id', 'unit_id');
  const stateList = states?.map((e) => ({ label: e, value: e }));
  const driversList = drivers?.data;

  const [latLng, setLatLng] = useState(null);
  const [error, setError] = useState('');

  const initialValues = useMemo(() => {
    if (iftaDetails?.iftaMileageId) {
      return {
        date: iftaDetails?.date,
        totalMiles: iftaDetails?.distanceTravelled,
        driver: driversList.find((e) => e?.value === iftaDetails?.driverId),
        endMeter: iftaDetails?.endOdometerReading || 0,
        startMeter: iftaDetails?.startOdometerReading || 0,
        state: { label: iftaDetails?.state, value: iftaDetails?.state },
        taxable: iftaDetails?.taxable ? 'active' : 'inactive',
        vehicle: { label: iftaDetails?.vehicleName, value: iftaDetails?.vehicleId },
      };
    }
    return { taxable: 'inactive' };
  }, [iftaDetails, driversList]);

  const schema = useMemo(() => {
    return getSchema({ iftaDetails });
  }, [iftaDetails]);

  const validatePayload = async (values, { isEdit, iftaDetails }) => {
    const validatePayload = {
      apiId: 'validateOdometer',
      param: {
        date: moment(values?.date).format(BACKEND_DATE_FORMAT),
        endOdometerReading: values?.endMeter,
        startOdometerReading: values?.startMeter,
        vehicleIdPub: values?.vehicle?.value,
        iftaMileageId: iftaDetails?.iftaMileageId,
      },
    };
    const prevData = {
      endMeter: iftaDetails?.endOdometerReading,
      startMeter: iftaDetails?.startOdometerReading,
    };
    const nextData = {
      endMeter: values?.endMeter,
      startMeter: values?.startMeter,
    };
    if ((isEdit && !isEqual(prevData, nextData)) || !isEdit) {
      return await validateOdometer(validatePayload);
    }
  };

  const getPayload = (values, { isEdit, iftaDetails }) => {
    const requestData = {
      date: moment(values?.date).format(BACKEND_DATE_FORMAT),
      distanceTravelled: values?.totalMiles,
      driverId: values?.driver?.value,
      endOdometerReading: values?.endMeter,
      startOdometerReading: values?.startMeter,
      state: values?.state?.value,
      taxable: values?.taxable === 'active' ? true : false,
      vehicleId: values?.vehicle?.value,
      city: values?.endCity,
      ...(isEdit && { iftaMileageId: iftaDetails?.iftaMileageId }),
      ...(((isEdit && iftaDetails?.isUnknownState) || (!isEdit && !iftaDetails)) && { ...latLng }),
    };

    return {
      apiId: isEdit ? 'editIftaStateApi' : 'addIftaStateApi',
      param: requestData,
      data: {},
      callback: callback,
    };
  };

  const handleSubmit = async (values, { isEdit, iftaDetails }) => {
    setError('');
    if ((!latLng || latLng?.error || latLng?.errors) && (!isEdit || (isEdit && iftaDetails?.isUnknownState))) {
      setError(i18n.t('iftaMileage.addressError'));
      return;
    }
    try {
      const validationResult = await validatePayload(values, { isEdit, iftaDetails });
      if (!validationResult?.data) {
        setError(validationResult?.error?.message);
        return;
      }

      addUpdateIFTAState(getPayload(values, { isEdit, iftaDetails })).then((res) => {
        if (res) {
          callback('success', res, () => {
            history.push(pageUrls?.iftaList);
          });
          return;
        }
        throw new Error('This operation failed.');
      });
    } catch (error) {
      setError(error?.message || '');
    }
  };

  return (
    <>
      <Form
        type="server"
        direction="column"
        initialValues={{ ...initialValues }}
        RenderComponent={RenderComponent}
        vehicleList={[...activeVehicles, ...deActivatedVehicles]}
        stateList={stateList}
        drivers={driversList}
        callback={callback}
        handleOnSubmit={(values) => handleSubmit(values, { isEdit, iftaDetails })}
        customProps={{
          Styled,
          isEdit: isEdit,
          setInitialValues: () => {},
          history,
          fetchLatLong,
          setError,
          setLatLng,
          errorMessage: error || '',
          ...(iftaDetails ? { isUnknownState: iftaDetails?.isUnknownState } : { isUnknownState: true }),
        }}
        handleOnCancel={() => {
          history.push(pageUrls?.reportMileageList);
        }}
        validationSchema={object().shape({
          ...schema,
        })}
      />
    </>
  );
};

StateForm.propTypes = {};

export default StateForm;
